import { type LayerKey, STANDARD_BRANCH } from '@festina-life/api'
import type { Optional } from '@festina-life/core'
import { Button } from '@festina-life/ui/actions'
import { Select } from '@festina-life/ui/forms'
import { useTranslate } from '@festina-life/ui/hooks'
import { Popover, PopoverButton } from '@festina-life/ui/structure'
import { useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router'

import { useMetaModelContext } from '../../context/metaModelContext'
import { useTranslateLayer } from '../../hooks/useTranslateLayer'
import { scratchpad } from '../../system/paths'
import { useNavigationParams } from '../../system/useNavigationParams'
import { useAllLayersOnServer } from '../../uiModels/api/metaModel/useAllLayersOnServer'
import { useOperationsContext } from '../../workspace/operationsContext'
import { useWorkspaceManagerContext } from '../../workspace/workspaceManagerContext'
import { ActionsMenuPanel } from '../actionsPanel/ActionsMenuPanel'
import { ToggleIconButton } from '../toggleIconButton/ToggleIconButton'

export const OpenScratchpadButton = () => {
  const { server, branch } = useNavigationParams()
  const { registeredTenantCreateOperations } = useOperationsContext()
  const serverLayers = useAllLayersOnServer({
    server,
    allTenants: registeredTenantCreateOperations,
  })
  const { blockTopology } = useMetaModelContext()
  const translate = useTranslate()
  const translateLayer = useTranslateLayer()
  const navigate = useNavigate()
  const { currentWorkspaceKey } = useWorkspaceManagerContext()
  const location = useLocation()
  const popoverButtonRef = useRef<HTMLButtonElement>(null)
  const shouldOpenEmptyContextScrachpad = !branch || branch === STANDARD_BRANCH
  const rootBlock = blockTopology.name // FIXME fix this once the scratchpad supports block selection.
  const layerOptions = serverLayers.get(rootBlock) ?? []
  const [selectedLayer, setSelectedLayer] = useState(layerOptions[0])

  const handleChangeSelectedLayer = (layer: Optional<LayerKey>) => {
    if (layer) {
      setSelectedLayer(layer)
    }
  }

  const handleNavigateToScratchpad = async (close?: () => void) => {
    if (selectedLayer) {
      await navigate(
        scratchpad({
          workspaceName: currentWorkspaceKey?.name,
          server,
          branch,
          layer: selectedLayer,
          block: rootBlock, // FIXME add support for selecting block in scratchpad
        }),
      )
      close?.()
    }
  }

  return shouldOpenEmptyContextScrachpad ? (
    <ToggleIconButton
      isActive={location.pathname.endsWith('scratchpad')}
      tip={translate('leftBar.button.scratchpad')}
      iconName="CommandLine"
      onClick={() => handleNavigateToScratchpad()}
    />
  ) : (
    <Popover>
      {({ close }) => (
        <>
          {/* React Aria's usePress stops click event propagation, preventing it from
          bubbling to PopoverButton's div. We work around this by programmatically
          dispatching a click on the div via ref, since usePress calls onClick
          before stopping propagation. */}
          <PopoverButton as="div" ref={popoverButtonRef}>
            <ToggleIconButton
              isActive={location.pathname.endsWith('scratchpad')}
              tip={translate('leftBar.button.scratchpad')}
              iconName="CommandLine"
              className="w-full"
              onClick={() => popoverButtonRef.current?.click()}
            />
          </PopoverButton>
          <ActionsMenuPanel
            anchor={{ to: 'right' }}
            className="max-w-lg! flex flex-col gap-2"
          >
            <Select
              options={layerOptions}
              renderFn={translateLayer}
              wrapperProps={{
                label: translate(
                  'serverDetailsPanel.tabs.manage.openScratchpad.layer.label',
                ),
                description: translate(
                  'serverDetailsPanel.tabs.manage.openScratchpad.layer.description',
                ),
              }}
              keyFn={(layer) => layer.toString()}
              shouldShowClearButton={false}
              value={selectedLayer}
              onChange={handleChangeSelectedLayer}
            />
            <Button
              onClick={() => handleNavigateToScratchpad(close)}
              className="self-end"
            >
              {translate(
                'serverDetailsPanel.tabs.manage.openScratchpad.button.open',
              )}
            </Button>
          </ActionsMenuPanel>
        </>
      )}
    </Popover>
  )
}
